/** The ERP prints header comment lines 40 characters wide, at most 10. */
const LINE_WIDTH = 40;
const MAX_LINES = 10;

/**
 * What makes two instructions the same one, as the server matches them: the
 * text uppercased without whitespace or the punctuation at its ends.
 */
export function instructionKey(lines: readonly string[]): string {
  return lines
    .join('')
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/^[:;*\-.,]+|[:;*\-.,]+$/g, '');
}

/** True when two sets of lines print the same. */
export function sameLines(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((line, index) => line === b[index]);
}

/** One paragraph as lines of at most 40 characters, a longer word cut. */
function wrapParagraph(paragraph: string): string[] {
  const words = paragraph
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((word) => word.match(new RegExp(`.{1,${LINE_WIDTH}}`, 'g')) ?? []);
  const lines: string[] = [];
  for (const word of words) {
    const last = lines.at(-1);
    if (last !== undefined && last.length + 1 + word.length <= LINE_WIDTH)
      lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  }
  return lines;
}

/**
 * Typed text as printed lines: each paragraph wrapped at a word boundary (a
 * longer word is cut), blank lines dropped, at most ten lines.
 */
export function wrapInstructionText(text: string): string[] {
  return text.split('\n').flatMap(wrapParagraph).slice(0, MAX_LINES);
}

export const NONE = 'none';
export const CUSTOM = 'custom';
const SHOWN = 5;

/**
 * What the shipping instructions picker shows: the selected entry (a saved
 * instruction's id, none, or custom text) and the saved instructions listed,
 * the first five unless all are asked for, plus the selected one.
 */
export function instructionChoices<
  T extends { id: string; lines: string[] },
>(params: {
  value: readonly string[];
  saved: readonly T[];
  typing: boolean;
  showAll: boolean;
}): { selected: string; listed: T[]; more: boolean } {
  const { value, saved } = params;
  const matched = saved.find(
    (row) => instructionKey(row.lines) === instructionKey(value),
  );
  const shown = params.showAll ? [...saved] : saved.slice(0, SHOWN);
  let selected = matched?.id ?? CUSTOM;
  if (value.length === 0) selected = NONE;
  if (params.typing) selected = CUSTOM;
  return {
    selected,
    listed: matched && !shown.includes(matched) ? [...shown, matched] : shown,
    more: !params.showAll && saved.length > SHOWN,
  };
}
